import React from 'react';
import { SearchableMultiSelect } from './SearchableMultiSelect';
import { useOrganizationMembers } from '../hooks/useOrganizationMembers';

export interface MemberMultiSelectProps {
  organizationId?: string;
  value: number[];
  onChange: (memberIds: number[]) => void;
  excludeUserIds?: number[];
  excludeSelf?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Member multi-select that automatically loads members from an organization.
 * Wraps SearchableMultiSelect with automatic member loading and filtering.
 *
 * @example
 * <MemberMultiSelect
 *   organizationId={selectedOrgId}
 *   value={selectedMemberIds}
 *   onChange={setSelectedMemberIds}
 *   excludeUserIds={alreadySharedWithUserIds}
 *   label="Share with Members"
 *   placeholder="Select members..."
 * />
 */
export const MemberMultiSelect: React.FC<MemberMultiSelectProps> = ({
  organizationId,
  value,
  onChange,
  excludeUserIds = [],
  excludeSelf = true,
  label = 'Members',
  placeholder = 'Select members...',
  required = false,
  disabled = false,
  className = '',
}) => {
  const { members, loading, error } = useOrganizationMembers({
    organizationId,
    excludeUserIds,
    excludeSelf,
  });

  return (
    <div className={className}>
      {label && (
        <label className="text-sm font-medium text-text-secondary">
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}

      {error && (
        <div className="py-1.5 px-2.5 rounded-[var(--radius-base)] text-xs text-[var(--theme-danger)] bg-[rgba(220,38,38,0.08)] border border-[rgba(220,38,38,0.2)] mb-2">{error}</div>
      )}

      <SearchableMultiSelect
        options={members}
        selectedIds={value}
        onChange={onChange}
        placeholder={loading ? 'Loading members...' : placeholder}
        disabled={disabled || loading || !organizationId}
        emptyMessage={
          !organizationId
            ? 'Select an organization first'
            : members.length === 0
            ? 'No available members'
            : 'No members found'
        }
        searchPlaceholder="Search members..."
        loading={loading}
      />

      {!organizationId && (
        <small className="block text-[var(--theme-text-tertiary)] text-xs mt-1">
          Select an organization to see available members
        </small>
      )}
    </div>
  );
};
